import React, { useRef, useState, useEffect, ReactNode, useCallback } from 'react';
import VideoContext from './VideoContext'
//@ts-ignore
import AiNumberReader from './AiNumberReader.worker'

interface VideoProviderProps {
  children: ReactNode;
}

// window.BarcodeDetector isn't reliable on Safari - a polyfill loaded elsewhere in the app
// can install it too, so we can't just feature-detect.
function isSafariBrowser(): boolean {
  return (window.navigator.vendor || '').includes('Apple')
}

// a frame the worker hasn't answered within this time is treated as lost and the worker is replaced;
// the first decodes on a phone can take over 2 s
const WORKER_FRAME_TIMEOUT_MS = 6000
// frames are scaled down to at most this size for QR decoding; the photo still uses the full-size canvas
const QR_DECODE_MAX_DIMENSION = 640
// how often the QR scanning summary is written to the console
const QR_LOG_INTERVAL_MS = 2000

// part of the video shown on a canvas of the given size, which crops the video to fill it (same sums as the preview)
function visibleVideoCrop(videoWidth: number, videoHeight: number, canvasWidth: number, canvasHeight: number) {
  const crop = { x: 0, y: 0, width: videoWidth, height: videoHeight }
  if (!canvasWidth || !canvasHeight) return crop
  const videoAspectRatio = videoWidth / videoHeight
  const canvasAspectRatio = canvasWidth / canvasHeight
  if (canvasAspectRatio < videoAspectRatio) {
    crop.width = videoHeight * canvasAspectRatio
    crop.x = (videoWidth - crop.width) / 2
  } else {
    crop.height = videoWidth / canvasAspectRatio
    crop.y = (videoHeight - crop.height) / 2
  }
  return crop
}

const VideoProvider: React.FC<VideoProviderProps> = ({ children }) => {

  const [cameraState, setCameraState] = useState<string>('IDLE')
  const [selectedCamera, setSelectedCamera] = useState<string | null>(null)
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([])
  const [QRCodesPresent, setQRCodesPresent] = useState<any>(null)
  const [QRCodeSearchOptions, setQRCodesSearchOptions] = useState<any>(null)
  const [isFacingUser, setIsFacingUser] = useState<boolean>(false)
  const [zoom, setZoom] = useState<number>(1)
  const [longExposureMode, setLongExposureMode] = useState<boolean>(false)

  const animationFrameHandleRef = useRef<number>(0) // this is needed for cancel
  const canvasesToStreamToRef = useRef<React.RefObject<HTMLCanvasElement>[]>([])


  const mediaStreamRef = useRef<MediaStream | null>(null)
  const frameSinceQRCodeSpotted = useRef<number>(0)
  const QRCodeSearchOptionsRef = useRef<any>(null)
  const videoStartLockRef = useRef<string | null>(null)
  const cameraStateRef = useRef<string | null>(null)

  const webWorkerRef = useRef<any>(null)
  const workerFrameSentAtRef = useRef<number | null>(null)
  const qrDecodeCanvasRef = useRef<HTMLCanvasElement | null>(null)
  // counts since the last QR summary log
  const qrStatsRef = useRef({ lastLogAt: 0, ticks: 0, videoReadyTicks: 0, framesSent: 0, framesSkipped: 0, results: 0, codesFound: 0, decodeMsTotal: 0, lastCode: '' })

  const videoElementRef = useRef<HTMLVideoElement>(null)
  const rawVideoCanvasRef = useRef<HTMLCanvasElement>(null)
  const captureCanvasRef = useRef<HTMLCanvasElement>(null)
  const rotatedCanvasRef = useRef<HTMLCanvasElement>(null)


  const barcodeModeRef = useRef<string>(null)
  const barcodeDetectorRef = useRef<any>(null)
  const barcodeDetectBusyRef = useRef<boolean>(false)
  const isFacingUserRef = useRef<any>(null)
  const tickZoom = useRef<number>(1)
  const tickLongExposureMode = useRef<boolean>(true)


  useEffect(() => { QRCodeSearchOptionsRef.current = QRCodeSearchOptions }, [QRCodeSearchOptions])
  useEffect(() => { isFacingUserRef.current = isFacingUser }, [isFacingUser])
  useEffect(() => { cameraStateRef.current = cameraState }, [cameraState])
  useEffect(() => { console.log(`[QR] camera state: ${cameraState}`) }, [cameraState])
  useEffect(() => { console.log(`[QR] QRCodesPresent: ${JSON.stringify(QRCodesPresent)}`) }, [QRCodesPresent])
  useEffect(() => { console.log(`[QR] search options: ${JSON.stringify(QRCodeSearchOptions)}`) }, [QRCodeSearchOptions])
  useEffect(() => { tickZoom.current = zoom }, [zoom])
  useEffect(() => { tickLongExposureMode.current = longExposureMode }, [longExposureMode])

  useEffect(() => {
    if (webWorkerRef.current === null) {
      createWorker()
      if ("BarcodeDetector" in window && !isSafariBrowser()) {
        //@ts-ignore
        barcodeDetectorRef.current = new window.BarcodeDetector({
          formats: ["qr_code"]
        });
        //@ts-ignore
        barcodeModeRef.current = "browser"
      } else {
        //@ts-ignore
        barcodeModeRef.current = "jsqr"
      }

    }

  }, [])

  useEffect(() => {
    function handleVisibilityChange() {
      if (document.hidden) {
        if (cameraStateRef.current === 'RUNNING') {
          stopCamera()
          setCameraState('PAUSED_TAB_INACTIVE')
          cameraStateRef.current = 'PAUSED_TAB_INACTIVE'
        }
        //console.log('Tab is inactive.');
      } else {
        //console.log('Tab is active.');
        if (cameraStateRef.current === 'PAUSED_TAB_INACTIVE') {
          // startCamera() itself transitions to RUNNING once the stream is
          // actually playing - don't claim RUNNING before that happens
          startCamera()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return (() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    })

  }, [])



  useEffect(() => {
    const updateAvailableCameras = async () => {
      const videoDevices = await _getAvailableDevices()
      setAvailableCameras(videoDevices)
    }
    updateAvailableCameras()
    // some android phones (e.g. our Hauwei) change their mind about what cameras they have a short time after we first ask
    setTimeout(updateAvailableCameras, 1000)
    if (navigator && navigator.mediaDevices) {
      navigator.mediaDevices.ondevicechange = updateAvailableCameras
      return (() => {
        navigator.mediaDevices.removeEventListener('devicechange', updateAvailableCameras);
      })
    }
  }, [])



  const tick = useCallback((frameCount: number) => {
    const videoElement = videoElementRef.current!
    const beta = 0.2

    let newFrameCount = frameCount
    let isVideoPlayingRightNow = false
    if (canvasesToStreamToRef.current.length > 0) {
      newFrameCount = frameCount + 1
      isVideoPlayingRightNow = true
    }

    const stats = qrStatsRef.current
    stats.ticks++
    const now = Date.now()
    if (now - stats.lastLogAt >= QR_LOG_INTERVAL_MS) {
      if (stats.lastLogAt !== 0) {
        const averageDecode = stats.results ? Math.round(stats.decodeMsTotal / stats.results) : 0
        const waiting = workerFrameSentAtRef.current === null ? 'no' : `${now - workerFrameSentAtRef.current}ms`
        console.log(`[QR] last ${now - stats.lastLogAt}ms: camera=${cameraStateRef.current} canvases=${canvasesToStreamToRef.current.length} readyState=${videoElement ? videoElement.readyState : 'none'} video=${videoElement ? `${videoElement.videoWidth}x${videoElement.videoHeight}` : 'none'} preview=${canvasesToStreamToRef.current[0]?.current ? `${canvasesToStreamToRef.current[0].current!.width}x${canvasesToStreamToRef.current[0].current!.height}` : 'none'} decode=${qrDecodeCanvasRef.current ? `${qrDecodeCanvasRef.current.width}x${qrDecodeCanvasRef.current.height}` : 'none'} search=${!!(QRCodeSearchOptionsRef.current && QRCodeSearchOptionsRef.current['enabled'])} mode=${barcodeModeRef.current} ticks=${stats.ticks} readyTicks=${stats.videoReadyTicks} sent=${stats.framesSent} skippedWorkerBusy=${stats.framesSkipped} results=${stats.results} codes=${stats.codesFound} avgDecode=${averageDecode}ms waitingForWorker=${waiting}`)
      }
      qrStatsRef.current = { ...stats, lastLogAt: now, ticks: 0, videoReadyTicks: 0, framesSent: 0, framesSkipped: 0, results: 0, codesFound: 0, decodeMsTotal: 0 }
    }

    if (isVideoPlayingRightNow && videoElement.readyState === videoElement.HAVE_ENOUGH_DATA) {
      qrStatsRef.current.videoReadyTicks++
      const video_width = videoElement.videoWidth
      const video_height = videoElement.videoHeight

      const rawVideoCanvas = rawVideoCanvasRef.current
      const raw_ctx = rawVideoCanvas!.getContext("2d")
      const qr_search_options = QRCodeSearchOptionsRef.current || {}
      const look_for_qr_codes = qr_search_options && qr_search_options['enabled']

      if (look_for_qr_codes) {
        // scaled video height should be no more than 480x480
        const max_dimension = 2160
        let scale_factor = 1
        if (video_width > video_height) {
          if (video_width > max_dimension) {
            scale_factor = max_dimension / video_width
          }
        } else {
          if (video_height > max_dimension) {
            scale_factor = max_dimension / video_height
          }
        }

        const scaled_video_width = Math.ceil(video_width * scale_factor)
        const scaled_video_height = Math.ceil(video_height * scale_factor)

        if (rawVideoCanvas!.width !== scaled_video_width || rawVideoCanvas!.height !== scaled_video_height) {
          rawVideoCanvas!.width = scaled_video_width
          rawVideoCanvas!.height = scaled_video_height
        }

        // only the part of the video the user can see on the preview is searched; searchArea is a fraction of that
        const previewCanvas = canvasesToStreamToRef.current[0]?.current
        const crop = visibleVideoCrop(video_width, video_height, previewCanvas?.width || 0, previewCanvas?.height || 0)
        const searchArea = {
          left: crop.x * scale_factor,
          top: crop.y * scale_factor,
          width: crop.width * scale_factor,
          height: crop.height * scale_factor
        }

        if (qr_search_options && qr_search_options['searchArea']) {
          const scaleFactors = qr_search_options['searchArea']
          searchArea.left += searchArea.width * scaleFactors.left
          searchArea.top += searchArea.height * scaleFactors.top
          searchArea.width = searchArea.width * scaleFactors.width
          searchArea.height = searchArea.height * scaleFactors.height
        }


        // This is the video that gets snapshotted as the photo

        // apply scaling to raw video, keeping it centred
        raw_ctx!.save()
        raw_ctx!.translate(rawVideoCanvas!.width / 2, rawVideoCanvas!.height / 2);
        raw_ctx!.scale(tickZoom.current, tickZoom.current)
        raw_ctx!.translate(-rawVideoCanvas!.width / 2, -rawVideoCanvas!.height / 2);

        if (tickLongExposureMode.current) {
          raw_ctx!.globalCompositeOperation = "source-over";
          raw_ctx!.globalAlpha = beta;
          raw_ctx!.drawImage(videoElement, 0, 0, rawVideoCanvas!.width, rawVideoCanvas!.height)
        } else {

          raw_ctx!.drawImage(videoElement, 0, 0, rawVideoCanvas!.width, rawVideoCanvas!.height)

        }

        raw_ctx!.restore()

        if (raw_ctx) {
          // search area copied into a smaller canvas so decoding takes fewer pixels
          if (!qrDecodeCanvasRef.current) qrDecodeCanvasRef.current = document.createElement('canvas')
          const qrDecodeCanvas = qrDecodeCanvasRef.current
          const decodeScale = Math.min(1, QR_DECODE_MAX_DIMENSION / Math.max(searchArea.width, searchArea.height))
          const decodeWidth = Math.max(1, Math.round(searchArea.width * decodeScale))
          const decodeHeight = Math.max(1, Math.round(searchArea.height * decodeScale))
          if (qrDecodeCanvas.width !== decodeWidth || qrDecodeCanvas.height !== decodeHeight) {
            qrDecodeCanvas.width = decodeWidth
            qrDecodeCanvas.height = decodeHeight
          }
          const decode_ctx = qrDecodeCanvas.getContext('2d', { willReadFrequently: true })!
          decode_ctx.drawImage(rawVideoCanvas!, searchArea.left, searchArea.top, searchArea.width, searchArea.height, 0, 0, decodeWidth, decodeHeight)
          const rawVideoImageData = decode_ctx.getImageData(0, 0, decodeWidth, decodeHeight)

          if (rawVideoImageData) {
            if (barcodeModeRef.current === 'browser') {
              // skip this frame while the last detect is still running
              if (!barcodeDetectBusyRef.current) {
                barcodeDetectBusyRef.current = true
                barcodeDetectorRef.current.detect(rawVideoImageData).then((detector_result: any[]) => {
                  if (detector_result.length !== 0) {
                    const first_result = detector_result[0]
                    const code = first_result.rawValue
                    webWorkerRef.current.postMessage({ 'action': 'reportCodeFound', 'code': code })
                    sendFrameToWorker(rawVideoImageData)
                  }
                }).catch((err: any) => {
                  console.error(`Barcode detect failed: ${err}`)
                }).finally(() => {
                  barcodeDetectBusyRef.current = false
                })
              }
            } else {
              sendFrameToWorker(rawVideoImageData)
            }
          }
        }
      }


      for (const canvasRef of canvasesToStreamToRef.current) {
        const canvas = canvasRef.current
        const ctx = canvas!.getContext("2d")
        if (ctx) {

          const canvas_width = canvas!.width
          const canvas_height = canvas!.height
          const video_aspect_ratio = video_width / video_height
          const canvas_aspect_ratio = canvas_width / canvas_height

          let render_width = canvas_width
          let render_height = canvas_width / video_aspect_ratio
          let cropped_video_width = video_width
          let cropped_video_height = video_height
          let x_adjust = 0
          let y_adjust = 0


          if (canvas_aspect_ratio < video_aspect_ratio) {
            render_height = canvas_height
            render_width = canvas_height * video_aspect_ratio
          }

          if (render_width > canvas_width) {
            const overflow_as_fraction = 1 - (canvas_width / render_width)
            cropped_video_width = video_width * (1 - overflow_as_fraction)
            x_adjust = (overflow_as_fraction / 2) * video_width
            render_width = canvas_width
          }
          if (render_height > canvas_height) {
            const overflow_as_fraction = 1 - (canvas_height / render_height)
            cropped_video_height = video_height * (1 - overflow_as_fraction)
            y_adjust = (overflow_as_fraction / 2) * video_height
            render_height = canvas_height
          }


          ctx.save();
          // apply zoom, keeping centred and mirrored if front camera
          ctx.translate(render_width / 2, render_height / 2);
          ctx.scale(tickZoom.current * (isFacingUserRef.current ? -1 : 1), tickZoom.current);
          ctx.translate(-render_width / 2, -render_height / 2);
          // this is the image cropped to the phone screen and window

          if (tickLongExposureMode.current) {
            ctx.save();
            ctx.globalCompositeOperation = "source-over";
            ctx.globalAlpha = beta;
            ctx.drawImage(videoElement, x_adjust, y_adjust, cropped_video_width, cropped_video_height, 0, 0, render_width, render_height);
            ctx.restore();
          } else {
            ctx.drawImage(videoElement, x_adjust, y_adjust, cropped_video_width, cropped_video_height, 0, 0, render_width, render_height)

          }

          ctx.restore();


          if (qr_search_options && qr_search_options['enabled'] && qr_search_options['searchArea'] && qr_search_options['showSearchArea']) {
            const scaleFactors = qr_search_options['searchArea']
            const searchArea = { top: 0, left: 0, width: 0, height: 0 }
            searchArea.top = Math.ceil(render_height * scaleFactors.top)
            searchArea.left = Math.ceil(render_width * scaleFactors.left)
            searchArea.width = Math.ceil(render_width * scaleFactors.width)
            searchArea.height = Math.ceil(render_height * scaleFactors.height)
            ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'; // Red color with 50% opacity
            ctx.fillRect(0, 0, render_width, searchArea.top)
            ctx.fillRect(0, searchArea.height + searchArea.top, render_width, searchArea.height)
            ctx.fillRect(0, searchArea.top, searchArea.left, searchArea.height)
            ctx.fillRect(searchArea.left + searchArea.width, searchArea.top, (render_width - (searchArea.width + searchArea.left)), searchArea.height)

          }

        }
      }
    }
    animationFrameHandleRef.current = requestAnimationFrame(() => { tick(newFrameCount) })
  }, [])

  useEffect(() => {
    tick(0)
    return (() => { cancelAnimationFrame(animationFrameHandleRef.current) })
  }, [tick]);


  const getDomainFromURL = (urlString: string) => {
    if (urlString === "") {
      return ""
    }
    const url = new URL(urlString);
    const domain = url.hostname;
    return domain
  }

  const createWorker = () => {
    const worker = new AiNumberReader()
    worker.onmessage = handleMessageFromWebWorker
    worker.onerror = (event: ErrorEvent) => {
      console.error(`[QR] worker error: ${event.message}`)
      replaceWorker()
    }
    webWorkerRef.current = worker
    workerFrameSentAtRef.current = null
  }

  const replaceWorker = () => {
    if (webWorkerRef.current) {
      webWorkerRef.current.terminate()
    }
    createWorker()
  }

  // sends one frame at a time; the buffer is transferred, not copied
  const sendFrameToWorker = (imageData: ImageData) => {
    const sentAt = workerFrameSentAtRef.current
    if (sentAt !== null) {
      if (Date.now() - sentAt < WORKER_FRAME_TIMEOUT_MS) {
        qrStatsRef.current.framesSkipped++
        return
      }
      console.error(`[QR] worker did not answer in ${WORKER_FRAME_TIMEOUT_MS}ms, replacing it`)
      replaceWorker()
    }
    workerFrameSentAtRef.current = Date.now()
    qrStatsRef.current.framesSent++
    webWorkerRef.current.postMessage({ 'action': 'findQRCode', 'imageData': imageData }, [imageData.data.buffer])
  }

  const handleMessageFromWebWorker = (message: any) => {
    const messageData = message.data
    const action = messageData.action
    if (messageData.isFrameResult) {
      workerFrameSentAtRef.current = null
      qrStatsRef.current.results++
      qrStatsRef.current.decodeMsTotal += messageData.decodeMs || 0
    }
    const qr_search_options = QRCodeSearchOptionsRef.current!
    switch (action) {
      case 'qrCodeFound':
        //     console.log(' QR code found')
        const code = messageData.code
        qrStatsRef.current.codesFound++
        let domainValid = true
        if (qr_search_options && qr_search_options['expectedDomains']) {
          const qr_domain = getDomainFromURL(code.data)
          domainValid = false
          for (const validDomain of qr_search_options['expectedDomains']) {
            if (validDomain.toLowerCase() === qr_domain.toLowerCase()) {
              domainValid = true
            }
          }
        }
        // logged when the decoded value or the outcome changes, not on every frame
        const outcome = `${code.data} domainValid=${domainValid} camera=${cameraStateRef.current}`
        if (outcome !== qrStatsRef.current.lastCode) {
          console.log(`[QR] decoded ${outcome}`)
          qrStatsRef.current.lastCode = outcome
        }
        if (domainValid && (cameraStateRef.current === 'RUNNING')) {
          setQRCodesPresent([messageData.code.data])
          frameSinceQRCodeSpotted.current = 0
          //  console.log('💚')
          //  console.log(JSON.stringify(qr_search_options))

        } else {
          console.log(`[QR] ignoring code: domainValid=${domainValid} camera=${cameraStateRef.current}`)
        }

        break;
      case 'noQRCodeFound':
        frameSinceQRCodeSpotted.current = frameSinceQRCodeSpotted.current + 1
        if (frameSinceQRCodeSpotted.current === 30) {
          console.log('[QR] no code for 30 results, clearing QRCodesPresent')
          qrStatsRef.current.lastCode = ''
          setQRCodesPresent(null)
        }
        break;

      default:
        console.log(`got unknown message from worker ${action}`)
        break;
    }
  }

  const _getAvailableDevices = async () => {
    if(!navigator || !navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      console.log("enumerateDevices() not supported.");
      return []
    }
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(device => device.kind === 'videoinput');
    return videoDevices
  }


  const chooseCameraAndGetVideoConstraints = (videoDevices: MediaDeviceInfo[]) => {
    const screen_height = window.screen.height  //  window.innerHeight
    const screen_width = window.screen.width
    let window_width = window.innerWidth
    const screen_aspect_ratio = screen_width / screen_height
    //   const aspect_ratio_to_use = (screen_height > screen_width) ? 1 / screen_aspect_ratio : screen_aspect_ratio
    const aspect_ratio_to_use = 1
    window_width = window_width * 4
    const max_width = 1280
    if (window_width > max_width) {
      window_width = max_width
    }
    let deviceId = videoDevices[videoDevices.length - 1] && videoDevices[videoDevices.length - 1]['deviceId']
    if (['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform)) {
      if (videoDevices.length >= 2) {
        deviceId = videoDevices[1]['deviceId']
      }
    }

    const options: any = {
      width: { 'max': window_width, 'ideal': window_width },
      aspectRatio: { 'exact': aspect_ratio_to_use },
      frameRate: { 'max': 30, 'ideal': 30 }
    }

    const validDeviceIds = []
    for (const device of availableCameras) {
      validDeviceIds.push(device.deviceId)
    }
    const deviceFromLocalStorage = localStorage.getItem('selectedVideoInputDevice')
    if (deviceFromLocalStorage && validDeviceIds.includes(deviceFromLocalStorage)) {
      options['deviceId'] = { 'exact': deviceFromLocalStorage }
    } else {
      if (deviceId && deviceId !== '') {
        options['deviceId'] = { 'exact': deviceId }
      }
      options['facingMode'] = 'environment'
    }

    return options
  }

  const clearCameraStateVariables = () => {
    setQRCodesPresent(null)
  }

  const stopCamera = () => {
    if (cameraStateRef.current !== 'IDLE') {
      cameraStateRef.current = 'IDLE'
      setCameraState('IDLE')
      clearCameraStateVariables()
      const stream = mediaStreamRef.current
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    }
  }

  const handleTrackEnded = (event: Event) => {
    //console.log(`🍍 🍍 🍍 🍍 🍍 Video track ended detected ${event}`)
    if (cameraState === 'RUNNING') {
      startCamera()
    }
  }

  const startCamera = async () => {
    // set state to starting as theres some async stuff going on and this funciton will return before the video has actually started
    const array = new Uint32Array(4);
    window.crypto.getRandomValues(array);
    const myLockId = JSON.stringify(array)
    if (videoStartLockRef.current === null) {
      videoStartLockRef.current = myLockId
    } else {
      return
    }

    setCameraState('STARTING')
    clearCameraStateVariables()

    let videoConstraints = null
    // use get user media to get camera stream
    videoConstraints = chooseCameraAndGetVideoConstraints(availableCameras)
    //console.log("Selected video constraints: ", videoConstraints)
    setSelectedCamera(videoConstraints['deviceId'])
    // console.log("🎥 Trying to get camera with id ", videoConstraints['deviceId'])
    // console.log(videoConstraints)
    await navigator.mediaDevices.getUserMedia({ video: videoConstraints }).then(async function (stream) {
      if (videoStartLockRef.current !== myLockId) {
        return
      }
      const videoElement = videoElementRef.current
      mediaStreamRef.current = stream
      videoElement!.srcObject = stream
      videoElement!.setAttribute("playsinline", "true") // required to tell iOS safari we don't want fullscreen
      if (videoStartLockRef.current !== myLockId) {
        return
      }
      //console.log(`▶️ Starting video ${cameraState} ${Date.now()}`)
      let hasStartedRunning = false
      const markRunning = () => {
        // runs once, for the stream this call started
        if (hasStartedRunning || mediaStreamRef.current !== stream) {
          return
        }
        hasStartedRunning = true
        videoElement!.removeEventListener('playing', markRunning)
        setCameraState('RUNNING')
        cameraStateRef.current = 'RUNNING'
        videoStartLockRef.current = null
        let cameraDeviceId = null
        let facingUser = false
        stream.getTracks().forEach(track => {

          const capabilities = track.getCapabilities();
          const settings = track.getSettings();
          // console.log("Camera capabilities:", capabilities)
          // console.log("Camera settings:", settings)
          if ((capabilities && capabilities.facingMode && capabilities.facingMode.includes('user')) || (capabilities && capabilities.facingMode && capabilities.facingMode.length === 0)) {
            facingUser = true
          }
          cameraDeviceId = settings.deviceId
          //console.log("Got camera", cameraDeviceId)

          track.addEventListener('ended', (event) => { handleTrackEnded(event) });
          setSelectedCamera(`${cameraDeviceId}`)
        });
        if (facingUser) {
          setIsFacingUser(true)
        } else {
          setIsFacingUser(false)
        }
      }
      // the video can start playing even when play() rejects, so the playing event also marks it running
      videoElement!.addEventListener('playing', markRunning)
      const playPromise = videoElement!.play()
      if (playPromise !== undefined) {
        playPromise.then(markRunning).catch((err) => {
          console.error(`Error playing video: ${err}`)
          if (videoStartLockRef.current === myLockId) {
            videoStartLockRef.current = null
          }
        })
      }

    }).catch(function (err) {
      console.error(`Error accessing camera: ${err}`)
      // if its a NotAllowedError, the user has denied access to the camera
      // we need to set a more specific error state here
      // if NotFoundError then the device has no camera
      // if NotReadableError then the camera may be already in use or may be broken

      if (err.name === 'NotAllowedError') {
        setCameraState('PERMISSION_DENIED')
        cameraStateRef.current = 'PERMISSION_DENIED'
      } else if (err.name === 'NotFoundError') {
        setCameraState('NO_CAMERA_FOUND')
        cameraStateRef.current = 'NO_CAMERA_FOUND'
      } else if (err.name === 'NotReadableError') {
        setCameraState('CAMERA_UNREADABLE')
        cameraStateRef.current = 'CAMERA_UNREADABLE'
      } else {
        setCameraState('ERROR')
        cameraStateRef.current = 'ERROR'
      }
      videoStartLockRef.current = null
    })

  }

  const streamToMyCanvas = (canvasRef: React.RefObject<HTMLCanvasElement>) => {
    //console.log('✅ STARTING THE CAMERA')
    if (canvasesToStreamToRef.current.length === 0) {
      startCamera();
    }
    if (canvasesToStreamToRef.current.indexOf(canvasRef) === -1) {
      canvasesToStreamToRef.current.push(canvasRef)
    }
  }

  const stopStreamingToMyCanvas = (canvasRef: React.RefObject<HTMLCanvasElement>) => {
    //console.log('⛔️ STOPPING THE CAMERA')
    canvasesToStreamToRef.current = canvasesToStreamToRef.current.filter(function (e) { return e !== canvasRef })
    if (canvasesToStreamToRef.current.length === 0) {
      stopCamera()
    }
  }

  const selectCamera = (cameraId: string) => {
    console.log("Changing camera")
    if (cameraId !== selectedCamera) {
      localStorage.setItem('selectedVideoInputDevice', cameraId)
      if (cameraState === 'RUNNING') {
        setCameraState('SWITCHING')
        stopCamera()
        // startCamera() itself transitions to RUNNING once the new stream is
        // actually playing - don't claim RUNNING before that happens
        startCamera()
      }
    }
  }


  const captureAsJPEGString = (width: number | undefined = undefined, orientation: string = 'unknown') => {
    console.log("Capturing image as JPEG string with width ", width)
    console.log('camera state is ', cameraStateRef.current)

    // a real frame only exists once the stream is confirmed RUNNING - anything else
    // (starting up, switching camera, paused, or a failure state) means there is no
    // live feed, so return nothing rather than a blank/stale/fabricated image
    if (cameraStateRef.current !== 'RUNNING') {
      return undefined
    }

    const rawVideoCanvas = rawVideoCanvasRef.current
    const captureCanvas = captureCanvasRef.current
    if (!rawVideoCanvas) {
      return undefined
    }
    if (width === undefined) {
      const imageString = rawVideoCanvas?.toDataURL('image/jpeg')
      return imageString
    } else {
      let scale_factor = width / rawVideoCanvas.width
      if (scale_factor > 1) {
        captureCanvas!.width = rawVideoCanvas.width
        captureCanvas!.height = rawVideoCanvas.height
      } else {
        captureCanvas!.width = width
        captureCanvas!.height = (rawVideoCanvas.height * scale_factor)
      }
      const captureCanvasCtx = captureCanvas?.getContext("2d")

      captureCanvasCtx!.drawImage(rawVideoCanvas, 0, 0, rawVideoCanvas.width, rawVideoCanvas.height, 0, 0, captureCanvas!.width, captureCanvas!.height)
      const canvas_width = captureCanvas!.width
      const canvas_height = captureCanvas!.height
      if (!rotatedCanvasRef.current) {
        console.error('No rotated canvas found in ref')
        return ""
      }
      if (orientation === 'left') {

        const rotatedCanvas = rotatedCanvasRef.current
        rotatedCanvas.width = canvas_height
        rotatedCanvas.height = canvas_width
        const rotatedCtx = rotatedCanvas.getContext('2d')
        rotatedCtx!.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2)
        rotatedCtx!.rotate(90 * Math.PI / 180)
        rotatedCtx!.translate(-rotatedCanvas.height / 2, -rotatedCanvas.width / 2)
        rotatedCtx!.drawImage(captureCanvas!, 0, 0)
        return rotatedCanvas.toDataURL('image/jpeg')
      } else if (orientation === 'right') {

        const rotatedCanvas = rotatedCanvasRef.current
        rotatedCanvas.width = canvas_height
        rotatedCanvas.height = canvas_width
        const rotatedCtx = rotatedCanvas.getContext('2d')
        rotatedCtx!.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2)
        rotatedCtx!.rotate((270 * Math.PI) / 180)
        rotatedCtx!.translate(-rotatedCanvas.height / 2, -rotatedCanvas.width / 2)
        rotatedCtx!.drawImage(captureCanvas!, 0, 0)
        return rotatedCanvas.toDataURL('image/jpeg')
      } else if (orientation === 'top') {
        const rotatedCanvas = rotatedCanvasRef.current
        rotatedCanvas.width = canvas_width
        rotatedCanvas.height = canvas_height
        const rotatedCtx = rotatedCanvas.getContext('2d')
        rotatedCtx!.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2)
        rotatedCtx!.rotate(180 * Math.PI / 180)
        rotatedCtx!.translate(-rotatedCanvas.width / 2, -rotatedCanvas.height / 2)
        rotatedCtx!.drawImage(captureCanvas!, 0, 0)
        return rotatedCanvas.toDataURL('image/jpeg')
      }



      const imageString = captureCanvas?.toDataURL('image/jpeg')

      console.log(`
    scaling captured image from
    ${rawVideoCanvas.width}x${rawVideoCanvas.height} (${rawVideoCanvas.width / rawVideoCanvas.height})
    to:
    ${captureCanvas!.width}x${captureCanvas!.height} (${captureCanvas!.width / captureCanvas!.height}`)

      return imageString
    }
  }

  return (
    <VideoContext.Provider value={{
      stopStreamingToMyCanvas,
      streamToMyCanvas,
      availableCameras,
      selectCamera,
      selectedCamera,
      cameraState,
      QRCodesPresent,
      setQRCodesPresent,
      setQRCodesSearchOptions,
      captureAsJPEGString,
      clearCameraStateVariables,
      zoom,
      setZoom,
      setLongExposureMode,
      longExposureMode
    }}>
      <div className={'hidden'}>
        <video
          ref={videoElementRef}
          autoPlay={true}
          muted={true}
          playsInline={true}
        // style={{ outline: '2px solid red' }}
        ></video>
        <canvas ref={rawVideoCanvasRef}></canvas>
        <canvas ref={captureCanvasRef}></canvas>
        <canvas ref={rotatedCanvasRef}></canvas>

      </div>

      {children}

    </VideoContext.Provider>
  );
};

export default VideoProvider;
